import { Loader2 } from "lucide-react";
import { useCandidateEducation } from "@/pages/candidate-profile/hooks/useCandidateProfile";
import type { EducationDetail } from "@/interface/education";

export function EducationTab({
  profileId,
  education = [],
}: {
  profileId?: number | string;
  education?: EducationDetail[];
}) {
  const {
    data: eduQuery,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading,
  } = useCandidateEducation(profileId, { page: 1, page_size: 10 });

  const queryEdu = eduQuery?.pages?.flatMap((page: any) =>
    Array.isArray(page?.data) ? page.data : page?.data?.results || [],
  );

  const eduList: EducationDetail[] =
    queryEdu && queryEdu.length > 0
      ? queryEdu
      : Array.isArray(education)
        ? education
        : [];

  const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
    if (scrollHeight - scrollTop - clientHeight < 50) {
      if (hasNextPage && !isFetchingNextPage) {
        fetchNextPage();
      }
    }
  };

  return (
    <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-xl p-6 shadow-sm flex flex-col max-h-[750px]">
      <h2 className="text-lg font-bold text-primary mb-6 border-b border-slate-100 dark:border-slate-800 pb-4 shrink-0">
        Education
      </h2>

      {isLoading && eduList.length === 0 ? (
        <div className="flex justify-center items-center p-12">
          <Loader2 className="w-8 h-8 animate-spin text-purple-600" />
        </div>
      ) : (
        <div
          onScroll={handleScroll}
          className="overflow-y-auto pr-1 flex-1 space-y-4"
        >
          {eduList.length === 0 ? (
            <p className="text-sm text-slate-500 italic">
              No education records listed.
            </p>
          ) : (
            <div className="space-y-4">
              {eduList.map((edu: EducationDetail, i: number) => {
                const degreeName =
                  edu.degree?.degree_name || edu.course_name || "Degree";
                const instName = edu.institution || "Institution";
                const duration =
                  edu.start_year || edu.end_year
                    ? `${edu.start_year || ""} - ${edu.end_year || "Present"}`
                    : edu.startDate || edu.endDate || "";
                const gradeVal = edu.grade_cgpa || edu.grade || "";

                return (
                  <div
                    key={edu.id || i}
                    className="p-4 rounded-xl border border-slate-200 dark:border-slate-800 flex justify-between items-center"
                  >
                    <div>
                      <h3 className="font-bold text-slate-900 dark:text-white">
                        {degreeName}
                      </h3>
                      <p className="text-xs text-slate-500">{instName}</p>
                      {edu.fieldOfStudy && (
                        <p className="text-xs text-purple-600 dark:text-purple-400">
                          Field: {edu.fieldOfStudy}
                        </p>
                      )}
                    </div>
                    <div className="text-right">
                      {duration && (
                        <span className="text-xs font-semibold text-slate-500">
                          {duration}
                        </span>
                      )}
                      {gradeVal && (
                        <p className="text-xs text-emerald-600 font-bold">
                          Grade: {gradeVal}
                        </p>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {isFetchingNextPage && (
            <div className="flex justify-center items-center py-4">
              <Loader2 className="w-5 h-5 animate-spin text-purple-600" />
              <span className="ml-2 text-xs text-slate-500 font-medium">
                Loading more education records...
              </span>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
